import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import { useTranslation } from 'react-i18next'

// default column configuration
const default_COLUMNS = [
    { id: 'todo', title: 'To Do', color: 'gray' },
    { id: 'in-progress', title: 'In Progress', color: 'blue' },
    { id: 'review', title: 'In Review', color: 'yellow' },
    { id: 'done', title: 'Done', color: 'green' }
];

// Task priority badges
const PRIORITY_COLORS = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
};

// Column color mappings
const COLUMN_COLORS = {
    gray: {
        bg: 'bg-gray-50 dark:bg-gray-800',
        header: 'bg-gray-100 dark:bg-gray-700',
        border: 'border-gray-200 dark:border-gray-600',
        badge: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
    },
    blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/10',
        header: 'bg-blue-100 dark:bg-blue-900/30',
        border: 'border-blue-200 dark:border-blue-800',
        badge: 'bg-blue-200 text-blue-700 dark:bg-blue-800 dark:text-blue-300'
    },
    yellow: {
        bg: 'bg-yellow-50 dark:bg-yellow-900/10',
        header: 'bg-yellow-100 dark:bg-yellow-900/30',
        border: 'border-yellow-200 dark:border-yellow-800',
        badge: 'bg-yellow-200 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-300'
    },
    green: {
        bg: 'bg-green-50 dark:bg-green-900/10',
        header: 'bg-green-100 dark:bg-green-900/30',
        border: 'border-green-200 dark:border-green-800',
        badge: 'bg-green-200 text-green-700 dark:bg-green-800 dark:text-green-300'
    }
};

// Task Card Component
function TaskCard({ task, onDragStart, onDragEnd }) {
    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, task)}
            onDragEnd={onDragEnd}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing transition-all duration-200 hover:-translate-y-0.5"
        >
            {/* Task Title */}
            <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2 line-clamp-2">
                {task.title}
            </h4>

            {/* Task description */}
            {task.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                    {task.description}
                </p>
            )}

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                    {task.tags.map((tag, i) => (
                        <span
                            key={i}
                            className="px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 round"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                {/* Priority */}
                {task.priority && (
                    <span className={`px-2 py-0.5 text-xs font-medium round ${PRIORITY_COLORS[task.priority]}`}>
                        {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                )}

                {/* Assignee */}
                {task.assignee && (
                    <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-[10px] font-medium text-white">
                                {task.assignee.split(' ').map(n => n[0]).join('')}
                            </span>
                        </div>
                    </div>
                )}

                {/* Due Date */}
                {task.dueDate && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formatDate(task.dueDate)}
                    </span>
                )}
            </div>
        </div>
    );
}

// Column Component
function KanbanColumn({ column, tasks, onDragOver, onDrop, onDragStart, onDragEnd, isDraggedOver }) {
  const { t } = useTranslation('projectsMisc')
    const colorScheme = COLUMN_COLORS[column.color] || COLUMN_COLORS.gray;

    return (
        <div
            className={`flex flex-col min-w-[280px] max-w-[320px] rounded-xl border ${colorScheme.border} ${colorScheme.bg} transition-all duration-200 ${isDraggedOver ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
                }`}
            onDragOver={(e) => {
                e.preventDefault();
                onDragOver(column.id);
            }}
            onDrop={(e) => onDrop(e, column.id)}
            onDragLeave={() => onDragOver(null)}
        >
            {/* Column header */}
            <div className={`px-4 py-3 round-t-xl ${colorScheme.header}`}>
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                        {column.title}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorScheme.badge}`}>
                        {tasks.length}
                    </span>
                </div>
            </div>

            {/* Tasks List */}
            <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)]">
                {tasks.length > 0 ? (
                    tasks.map((task) => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                        />
                    ))
                ) : (
                    <div className="py-8 text-center">
                        <p className="text-sm text-gray-400 dark:text-gray-500">{t('no_tasks')}</p>
                    </div>
                )}
            </div>

            {/* Add Task Button */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                <button className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors flex items-center justify-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Task
                </button>
            </div>
        </div>
    );
}

// Main Kanban Board Component
export default function KanbanBoard({
    tasks = [],
    columns = default_COLUMNS,
    onTaskMove,
    projectId,
    projectName
}) {
    const [draggedTask, setDraggedTask] = useState(null);
    const [draggedOverColumn, setDraggedOverColumn] = useState(null);

    // Group tasks by status/column
    const getTasksByColumn = useCallback((columnId) => {
        return tasks.filter(task => task.status === columnId || task.columnId === columnId);
    }, [tasks]);

    const handleDragStart = useCallback((e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
        // Add some opacity to the dragged element
        e.target.style.opacity = '0.5';
    }, []);

    const handleDragEnd = useCallback((e) => {
        e.target.style.opacity = '1';
        setDraggedTask(null);
        setDraggedOverColumn(null);
    }, []);

    const handleDragOver = useCallback((columnId) => {
        setDraggedOverColumn(columnId);
    }, []);

    const handleDrop = useCallback((e, columnId) => {
        e.preventDefault();
        if (draggedTask && onTaskMove) {
            onTaskMove(draggedTask.id, columnId);
        }
        setDraggedTask(null);
        setDraggedOverColumn(null);
    }, [draggedTask, onTaskMove]);

    return (
        <div className="h-full">
            {/* Board header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {projectName ? `${projectName} - Kanban Board` : 'Kanban Board'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Drag and drop tasks to update their status
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {projectId && (
                        <Link
                            to={`/projects/${projectId}`}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                        >
                            ← Back to Project
                        </Link>
                    )}
                    <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                        + Add Task
                    </button>
                </div>
            </div>

            {/* Kanban Columns */}
            <div className="flex gap-4 overflow-x-auto pb-4">
                {columns.map((column) => (
                    <KanbanColumn
                        key={column.id}
                        column={column}
                        tasks={getTasksByColumn(column.id)}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        isDraggedOver={draggedOverColumn === column.id}
                    />
                ))}
            </div>
        </div>
    );
}
